import { getSearchSegmentPreviewProjection as makeSegmentPreviewProjection } from '../../utils/searchResultIdentity';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTabActivity } from '../../contexts/TabActivityContext';
import { ChevronLeft, ChevronRight, Film, LayoutGrid } from 'lucide-react';
import type { MarkerInfo, PromptRun, SearchResult, Video } from '../../services/api';
import { getSearchResultKey, getSearchResultRunId } from '../../utils/searchResultIdentity';
import {
  PreviewGrantProjection,
  useSearchWorkspace,
  SearchWorkspaceView,
} from '../../contexts/SearchWorkspaceContext';
import SearchResultCard, {
  getSearchResultCardEvidence,
} from '../SearchResultCard';
import type { SearchResultCardEvidence } from '../SearchResultCard';
import {
  PreviewViewportProvider,
  useIsNearPreviewViewport,
} from '../GrantedPreviewImage';

interface DeferredSearchResultCardProps {
  result: SearchResult;
  tileKey: string;
  onResultClick: (result: SearchResult) => void;
  onMarkerChange?: (result: SearchResult, marker: MarkerInfo) => void;
  selectedResultKey?: string | null;
  isVectorSearchTile?: boolean;
  videoTileEvidence?: SearchResultCardEvidence | SearchResultCardEvidence[] | null;
  prioritizedFieldPaths?: string[];
  previewProjection: PreviewGrantProjection | null;
}

const DeferredSearchResultCard = memo<DeferredSearchResultCardProps>(({
  result,
  tileKey,
  onResultClick,
  onMarkerChange,
  selectedResultKey,
  isVectorSearchTile,
  videoTileEvidence,
  prioritizedFieldPaths,
  previewProjection,
}) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const isNearViewport = useIsNearPreviewViewport(shellRef);
  return (
    <div ref={shellRef} className="search-gallery-tile-shell" data-tile-key={tileKey}>
      {isNearViewport ? (
        <SearchResultCard
          result={result}
          onResultClick={onResultClick}
          isActiveInSidebar={selectedResultKey === getSearchResultKey(result)}
          onMarkerChange={onMarkerChange}
          isVectorSearchTile={isVectorSearchTile}
          videoTileEvidence={videoTileEvidence}
          prioritizedFieldPaths={prioritizedFieldPaths}
          previewProjection={previewProjection}
        />
      ) : (
        <div className="search-gallery-tile-placeholder" aria-hidden="true" />
      )}
    </div>
  );
});

DeferredSearchResultCard.displayName = 'DeferredSearchResultCard';

const VideoBandHydrator = memo(({
  videoId,
  onEnsure,
}: {
  videoId: string;
  onEnsure: (videoId: string) => void;
}) => {
  const sentinelRef = useRef<HTMLSpanElement>(null);
  const { isActive } = useTabActivity();
  const isNearViewport = useIsNearPreviewViewport(sentinelRef);
  useEffect(() => {
    if (isActive && isNearViewport) {
      onEnsure(videoId);
    }
  }, [isActive, isNearViewport, onEnsure, videoId]);
  return <span ref={sentinelRef} className="search-video-band-hydrator" aria-hidden="true" />;
});

VideoBandHydrator.displayName = 'VideoBandHydrator';

interface ResultRunBand {
  runId: string;
  label: string;
  executedAt: string;
  results: SearchResult[];
}

interface ResultVideoRow {
  videoId: string;
  title: string;
  video: Video | null;
  bands: ResultRunBand[];
}

const UNSCOPED_BAND_ID = 'unscoped';
const EMPTY_PRIORITIZED_FIELD_PATHS: string[] = [];

const getMatchingSegmentCount = (band: ResultRunBand): number => (
  band.results.filter((result) => result.result_type !== 'video' && Boolean(result.segment_id)).length
);

interface VideoTilePresentation {
  previewProjection: PreviewGrantProjection | null;
  /** Undefined means there is no segment and the genuine video may use its own evidence. */
  evidence?: SearchResultCardEvidence[] | null;
}

interface VideoGridEntry {
  row: ResultVideoRow;
  band: ResultRunBand;
  bandIndex: number;
  targetHasMore: boolean;
  targetLoaded: boolean;
  presentation: VideoTilePresentation;
  videoResult: SearchResult;
  matchingSegmentCount: number;
  displayedEvidenceScore: number | null;
  originalIndex: number;
}

const getVideoTileEvidence = (
  result: SearchResult | null,
  prioritizedFieldPaths: string[],
): SearchResultCardEvidence[] | undefined => {
  if (!result) return undefined;
  const evidence = getSearchResultCardEvidence(result, prioritizedFieldPaths);
  const prioritizedEvidence = evidence.filter((item) => item.prioritized);
  return prioritizedEvidence.length > 0 ? prioritizedEvidence : evidence.slice(0, 1);
};

const getHighestDisplayedEvidenceScore = (
  evidence: SearchResultCardEvidence[],
): number | null => evidence.reduce<number | null>((highestScore, item) => {
  if (typeof item.score !== 'number' || !Number.isFinite(item.score)) {
    return highestScore;
  }
  return highestScore === null ? item.score : Math.max(highestScore, item.score);
}, null);

const normalizeDurablePreviewUri = (value: string | null | undefined): string | null => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || null;
};

const getVideoTilePresentation = (
  row: ResultVideoRow,
  band: ResultRunBand,
  prioritizedFieldPaths: string[],
  useBrowseVideoProjection: boolean,
): VideoTilePresentation => {
  const rankedSegments = band.results.filter((result) => (
    result.result_type !== 'video' && Boolean(result.segment_id)
  ));
  const topMatchingSegment = rankedSegments[0] || null;
  // Search and agent results use only their explicit parent-video family.
  // Browse starts from an authoritative Video row, whose own thumbnail/gif
  // fields are the same parent-video identity rather than segment media.
  const videoMedia = band.results[0] || null;
  const videoThumbnailGcsUri = normalizeDurablePreviewUri(
    useBrowseVideoProjection
      ? row.video?.thumbnail_gcs_uri
      : videoMedia?.video_thumbnail_gcs_uri,
  );
  const videoGifGcsUri = normalizeDurablePreviewUri(
    useBrowseVideoProjection
      ? row.video?.gif_gcs_uri
      : videoMedia?.video_gif_gcs_uri,
  );
  const previewProjection = videoThumbnailGcsUri || videoGifGcsUri
    ? {
        identity: { kind: 'video' as const, video_id: row.videoId },
        thumbnailGcsUri: videoThumbnailGcsUri,
        gifGcsUri: videoGifGcsUri,
      }
    : null;
  return {
    previewProjection,
    evidence: getVideoTileEvidence(topMatchingSegment, prioritizedFieldPaths),
  };
};

const makeVideoResult = (
  row: ResultVideoRow,
  band: ResultRunBand,
  options: { browse: boolean; hasMore: boolean; isLoaded: boolean },
  previewProjection: PreviewGrantProjection | null,
): SearchResult => {
  const representative = band.results[0];
  const videoBase = band.results.find((result) => result.result_type === 'video');
  const matchingSegmentCount = getMatchingSegmentCount(band);
  const videoThumbnailGcsUri = previewProjection?.thumbnailGcsUri || null;
  const videoGifGcsUri = previewProjection?.gifGcsUri || null;
  const emittedRunId = band.runId === UNSCOPED_BAND_ID ? undefined : band.runId;
  const syntheticVideoResultId = emittedRunId
    ? `video:${row.videoId}:${emittedRunId}`
    : `video:${row.videoId}`;
  if (videoBase) {
    return {
      ...videoBase,
      result_type: 'video',
      result_id: videoBase.result_id || syntheticVideoResultId,
      video_id: row.videoId,
      video_name: row.title,
      video_uri: row.video?.video_uri || videoBase.video_uri || null,
      segment_id: null,
      // Genuine backend video results own their representative playback
      // projection. Preserve it for the modal, while the tile uses only the
      // authoritative parent Video poster/montage pair below.
      segment_uri: videoBase.segment_uri || null,
      gcs_uri: videoBase.gcs_uri || null,
      preview_segment_id: videoBase.preview_segment_id || null,
      start_time: videoBase.start_time ?? null,
      end_time: videoBase.end_time ?? null,
      preview_start_time: videoBase.preview_start_time ?? null,
      preview_end_time: videoBase.preview_end_time ?? null,
      preview_segment_uri: videoBase.preview_segment_uri || null,
      preview_thumbnail_uri: videoBase.preview_thumbnail_uri || null,
      preview_gif_uri: videoBase.preview_gif_uri || null,
      video_thumbnail_gcs_uri: videoThumbnailGcsUri,
      video_thumbnail_uri: undefined,
      video_thumbnail_available: Boolean(videoThumbnailGcsUri),
      video_gif_gcs_uri: videoGifGcsUri,
      video_gif_uri: undefined,
      video_gif_available: Boolean(videoGifGcsUri),
      run_id: emittedRunId || videoBase.run_id || undefined,
      source_run_id: emittedRunId || videoBase.source_run_id || undefined,
      prompt_run_id: emittedRunId || videoBase.prompt_run_id || undefined,
      source_index_id: videoBase.source_index_id,
      marker: row.video?.marker || videoBase.marker,
    };
  }
  return {
    result_type: 'video',
    result_id: syntheticVideoResultId,
    video_id: row.videoId,
    video_name: row.title,
    video_uri: row.video?.video_uri || representative?.video_uri || null,
    segment_id: null,
    segment_uri: null,
    gcs_uri: null,
    preview_segment_id: null,
    start_time: null,
    end_time: null,
    preview_start_time: null,
    preview_end_time: null,
    preview_segment_uri: null,
    preview_thumbnail_uri: null,
    preview_gif_uri: null,
    thumbnail_gcs_uri: null,
    thumbnail_uri: undefined,
    thumbnail_data: undefined,
    thumbnail_available: false,
    gif_gcs_uri: null,
    gif_uri: undefined,
    gif_data: undefined,
    gif_available: false,
    video_thumbnail_gcs_uri: videoThumbnailGcsUri,
    video_thumbnail_uri: undefined,
    video_thumbnail_available: Boolean(videoThumbnailGcsUri),
    video_gif_gcs_uri: videoGifGcsUri,
    video_gif_uri: undefined,
    video_gif_available: Boolean(videoGifGcsUri),
    media_type: (row.video?.media_type as SearchResult['media_type'])
      || representative?.media_type
      || 'video',
    text_content: options.browse
      ? options.isLoaded
        ? `${matchingSegmentCount}${options.hasMore ? '+' : ''} segment${matchingSegmentCount === 1 && !options.hasMore ? '' : 's'} ${options.hasMore ? 'loaded' : 'available'}`
        : 'Segments loading'
      : `${matchingSegmentCount} matching segment${matchingSegmentCount === 1 ? '' : 's'}`,
    similarity_score: null,
    run_id: emittedRunId || representative?.run_id || undefined,
    source_run_id: emittedRunId || representative?.source_run_id || undefined,
    prompt_run_id: emittedRunId || representative?.prompt_run_id || undefined,
    source_index_id: representative?.source_index_id,
    marker: row.video?.marker,
  };
};

export interface SearchResultsGalleryProps {
  results: SearchResult[];
  videos?: Video[];
  runs?: PromptRun[];
  view: SearchWorkspaceView;
  onResultClick: (result: SearchResult) => void;
  onMarkerChange?: (result: SearchResult, marker: MarkerInfo) => void;
  selectedResultKey?: string | null;
  /** Prefix used only to distinguish repeated rendered Agent occurrences. */
  occurrenceContext?: string;
  selectedOccurrenceKey?: string | null;
  onResultOccurrenceClick?: (result: SearchResult, occurrenceKey: string) => void;
  includeEmptyVideoRows?: boolean;
  isVectorSearchTile?: boolean;
  prioritizedFieldPaths?: string[];
  hasMoreByTarget?: Record<string, boolean>;
  loadingTargetKey?: string | null;
  onLoadMoreBand?: (videoId: string, runId: string) => void;
  onEnsureVideoBands?: (videoId: string) => void;
  emptyMessage?: string;
  isPending?: boolean;
  /** Dense, single-execution preview; the surrounding execution owns its label. */
  compact?: boolean;
}

const SearchResultsGallery: React.FC<SearchResultsGalleryProps> = ({
  results,
  videos = [],
  runs = [],
  view,
  onResultClick,
  onMarkerChange,
  selectedResultKey,
  occurrenceContext,
  selectedOccurrenceKey,
  onResultOccurrenceClick,
  includeEmptyVideoRows = false,
  isVectorSearchTile,
  prioritizedFieldPaths = EMPTY_PRIORITIZED_FIELD_PATHS,
  hasMoreByTarget,
  loadingTargetKey,
  onLoadMoreBand,
  onEnsureVideoBands,
  emptyMessage = 'No results in this scope.',
  isPending = false,
  compact = false,
}) => {
  const { tileSize: workspaceTileSize } = useSearchWorkspace();
  const tileSize = compact ? 176 : workspaceTileSize;
  const [collapsedOlderRows, setCollapsedOlderRows] = useState<Set<string>>(new Set());
  const tileSizeStyle = useMemo(() => ({
    '--search-gallery-tile-width': `${tileSize}px`,
  }) as React.CSSProperties, [tileSize]);
  const videoMap = useMemo(() => new Map(videos.map((video) => [video.video_id, video])), [videos]);
  const runMap = useMemo(() => new Map(runs.map((run) => [run.run_id, run])), [runs]);
  const runVideoIds = useMemo(() => new Map(runs.map((run) => [
    run.run_id,
    new Set((run.run_context?.video_ids || []).flatMap((value: unknown) => {
      if (typeof value !== 'string') return [];
      const videoId = value.trim();
      return videoId ? [videoId] : [];
    })),
  ])), [runs]);

  const rows = useMemo<ResultVideoRow[]>(() => {
    const rowMap = new Map<string, ResultVideoRow>();
    if (includeEmptyVideoRows) {
      videos.forEach((video) => {
        const bands = runs
          .filter((run) => runVideoIds.get(run.run_id)?.has(video.video_id))
          .map((run) => ({
            runId: run.run_id,
            label: run.prompt_name || run.run_id,
            executedAt: run.executed_at || '',
            results: [],
          }));
        if (bands.length > 0) {
          rowMap.set(video.video_id, {
            videoId: video.video_id,
            title: video.title || video.video_id,
            video,
            bands,
          });
        }
      });
    }
    results.forEach((result) => {
      if (!result.video_id) return;
      let row = rowMap.get(result.video_id);
      if (!row) {
        const video = videoMap.get(result.video_id) || null;
        row = {
          videoId: result.video_id,
          title: result.video_name || video?.title || result.video_id,
          video,
          bands: [],
        };
        rowMap.set(result.video_id, row);
      }
      const runId = getSearchResultRunId(result) || UNSCOPED_BAND_ID;
      let band = row.bands.find((candidate) => candidate.runId === runId);
      if (!band) {
        const run = runMap.get(runId);
        band = {
          runId,
          label: run?.prompt_name || (runId === UNSCOPED_BAND_ID ? 'Results' : runId),
          executedAt: run?.executed_at || '',
          results: [],
        };
        row.bands.push(band);
      }
      band.results.push(result);
    });

    const unorderedRows = Array.from(rowMap.values());
    const videoOrder = includeEmptyVideoRows
      ? new Map(videos.map((video, index) => [video.video_id, index]))
      : null;
    const orderedRows = includeEmptyVideoRows
      ? unorderedRows.sort((left, right) => (
          (videoOrder?.get(left.videoId) ?? Number.MAX_SAFE_INTEGER)
          - (videoOrder?.get(right.videoId) ?? Number.MAX_SAFE_INTEGER)
        ))
      : unorderedRows;
    return orderedRows
      .map((row) => ({
        ...row,
        bands: [...row.bands].sort((left, right) => {
          const leftTime = Date.parse(left.executedAt) || 0;
          const rightTime = Date.parse(right.executedAt) || 0;
          return rightTime - leftTime;
        }),
      }));
  }, [includeEmptyVideoRows, results, runMap, runVideoIds, runs, videoMap, videos]);

  const videoGridEntries = useMemo<VideoGridEntry[]>(() => {
    if (view !== 'videos') return [];

    let originalIndex = 0;
    const entries = rows.flatMap((row) => {
      const collapseOlder = collapsedOlderRows.has(row.videoId);
      const visibleBands = collapseOlder ? row.bands.slice(0, 1) : row.bands;
      return visibleBands.map((band, bandIndex) => {
        const targetKey = `${row.videoId}::${band.runId}`;
        const targetHasMore = Boolean(hasMoreByTarget?.[targetKey]);
        const targetLoaded = !includeEmptyVideoRows
          || band.results.length > 0
          || Object.prototype.hasOwnProperty.call(hasMoreByTarget || {}, targetKey);
        const presentation = getVideoTilePresentation(
          row,
          band,
          prioritizedFieldPaths,
          includeEmptyVideoRows,
        );
        const videoResult = makeVideoResult(row, band, {
          browse: includeEmptyVideoRows,
          hasMore: targetHasMore,
          isLoaded: targetLoaded,
        }, presentation.previewProjection);
        // This mirrors SearchResultCard exactly: an explicit segment evidence
        // projection wins, while genuine video results fall back to their own
        // backend-provided field evidence. Hidden boolean fields never enter
        // this list and therefore cannot influence video-tile ordering.
        const displayedEvidence = presentation.evidence === undefined
          ? getSearchResultCardEvidence(videoResult, prioritizedFieldPaths)
          : presentation.evidence || [];
        return {
          row,
          band,
          bandIndex,
          targetHasMore,
          targetLoaded,
          presentation,
          videoResult,
          matchingSegmentCount: getMatchingSegmentCount(band),
          displayedEvidenceScore: getHighestDisplayedEvidenceScore(displayedEvidence),
          originalIndex: originalIndex++,
        };
      });
    });

    // Browse mode is canonically ordered by the index and must not jump while
    // its segment summaries hydrate. Search/agent tiles with visible backend
    // field scores are globally ranked; unscored tiles retain source order.
    if (includeEmptyVideoRows) return entries;
    return entries.sort((left, right) => {
      const leftHasScore = left.displayedEvidenceScore !== null;
      const rightHasScore = right.displayedEvidenceScore !== null;
      if (leftHasScore && rightHasScore) {
        const scoreDifference = (right.displayedEvidenceScore as number)
          - (left.displayedEvidenceScore as number);
        if (scoreDifference !== 0) return scoreDifference;
      } else if (leftHasScore !== rightHasScore) {
        return leftHasScore ? -1 : 1;
      }
      return left.originalIndex - right.originalIndex;
    });
  }, [
    collapsedOlderRows,
    hasMoreByTarget,
    includeEmptyVideoRows,
    prioritizedFieldPaths,
    rows,
    view,
  ]);

  if (isPending) {
    return (
      <div
        className="search-results-gallery search-results-gallery--video-grid"
        data-tile-size={tileSize}
        style={tileSizeStyle}
        aria-busy="true"
      >
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="search-gallery-tile-placeholder" aria-hidden="true" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="search-gallery-empty">
        <LayoutGrid size={36} />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  const toggleOlderRuns = (videoId: string) => {
    setCollapsedOlderRows((previous) => {
      const next = new Set(previous);
      if (next.has(videoId)) next.delete(videoId);
      else next.add(videoId);
      return next;
    });
  };

  if (view === 'videos') {
    return (
      <PreviewViewportProvider>
        <div
          className="search-results-gallery search-results-gallery--video-grid"
          data-view={view}
          data-tile-size={tileSize}
          style={tileSizeStyle}
          data-testid="video-results-grid"
          aria-label="Video results"
        >
          {videoGridEntries.map(({
          row,
          band,
          bandIndex,
          targetHasMore,
          targetLoaded,
          presentation,
          videoResult,
          matchingSegmentCount,
        }) => {
          const countLabel = !targetLoaded
            ? 'Loading segments'
            : matchingSegmentCount > 0 || targetHasMore
              ? `${matchingSegmentCount}${targetHasMore ? '+' : ''} segment${matchingSegmentCount === 1 && !targetHasMore ? '' : 's'}`
              : null;
          const tileKey = [occurrenceContext, 'video', row.videoId, band.runId]
            .filter(Boolean)
            .join(':');
          const hasOlderRuns = row.bands.length > 1;
          const collapseOlder = collapsedOlderRows.has(row.videoId);
          const showRunLabel = !compact && band.runId !== UNSCOPED_BAND_ID;

          return (
              <article
                className="search-video-grid-item"
                key={tileKey}
                data-video-id={row.videoId}
                data-run-id={band.runId}
                data-testid="video-grid-item"
                aria-label={`${row.title}, ${band.label}`}
              >
                {bandIndex === 0 && onEnsureVideoBands && (
                  <VideoBandHydrator videoId={row.videoId} onEnsure={onEnsureVideoBands} />
                )}
                <DeferredSearchResultCard
                  result={videoResult}
                  tileKey={tileKey}
                  onResultClick={(clicked) => (
                    onResultOccurrenceClick
                      ? onResultOccurrenceClick(clicked, tileKey)
                      : onResultClick(clicked)
                  )}
                  onMarkerChange={onMarkerChange}
                  selectedResultKey={occurrenceContext
                    ? (selectedOccurrenceKey === tileKey ? getSearchResultKey(videoResult) : null)
                    : selectedResultKey}
                  videoTileEvidence={presentation.evidence}
                  prioritizedFieldPaths={prioritizedFieldPaths}
                  previewProjection={presentation.previewProjection}
                />
                <div className="search-video-grid-meta">
                  <div className="search-video-grid-title-line">
                    <h3 title={row.title}>{row.title}</h3>
                    {countLabel && <span>{countLabel}</span>}
                  </div>
                  {(showRunLabel || (bandIndex === 0 && hasOlderRuns)) && (
                    <div className="search-video-grid-run-line">
                      {showRunLabel && (
                        <span className="search-video-grid-run" title={band.label}>
                          <Film size={12} aria-hidden="true" />
                          <span>{band.label}</span>
                        </span>
                      )}
                      {bandIndex === 0 && hasOlderRuns && (
                        <button
                          type="button"
                          className="search-video-grid-run-toggle"
                          aria-label={`${collapseOlder ? 'Show all runs' : 'Collapse older runs'} for ${row.title}`}
                          onClick={() => toggleOlderRuns(row.videoId)}
                        >
                          {collapseOlder ? `+${row.bands.length - 1} run${row.bands.length === 2 ? '' : 's'}` : 'Newest only'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </article>
          );
          })}
        </div>
      </PreviewViewportProvider>
    );
  }

  return (
    <PreviewViewportProvider>
      <div
        className="search-results-gallery"
        data-view={view}
        data-tile-size={tileSize}
        style={tileSizeStyle}
        data-testid="segment-results-gallery"
      >
      {rows.map((row) => {
        const collapseOlder = collapsedOlderRows.has(row.videoId);
        const visibleBands = collapseOlder ? row.bands.slice(0, 1) : row.bands;
        return (
          <article
            className="search-video-row"
            key={row.videoId}
            data-video-id={row.videoId}
            aria-label={`${row.title} results`}
          >
            {onEnsureVideoBands && (
              <VideoBandHydrator videoId={row.videoId} onEnsure={onEnsureVideoBands} />
            )}
            <header className="search-video-row-header">
              <div className="search-video-row-heading">
                <h3>{row.title}</h3>
                {!compact && <span>{row.bands.length} run{row.bands.length === 1 ? '' : 's'}</span>}
              </div>
              <span className="search-video-row-rule" data-testid="video-row-rule" aria-hidden="true" />
              {row.bands.length > 1 && (
                <button
                  type="button"
                  className="search-gallery-secondary-action"
                  onClick={() => toggleOlderRuns(row.videoId)}
                >
                  {collapseOlder ? 'Show all runs' : 'Collapse older runs'}
                </button>
              )}
            </header>

            {visibleBands.map((band) => {
              const targetKey = `${row.videoId}::${band.runId}`;
              const targetLoaded = !includeEmptyVideoRows
                || band.results.length > 0
                || Object.prototype.hasOwnProperty.call(hasMoreByTarget || {}, targetKey);
              return (
                <RunBand
                  key={`${row.videoId}:${band.runId}`}
                  videoId={row.videoId}
                  band={band}
                  selectedResultKey={selectedResultKey}
                  occurrenceContext={occurrenceContext}
                  selectedOccurrenceKey={selectedOccurrenceKey}
                  onResultOccurrenceClick={onResultOccurrenceClick}
                  onResultClick={onResultClick}
                  onMarkerChange={onMarkerChange}
                  isVectorSearchTile={isVectorSearchTile}
                  prioritizedFieldPaths={prioritizedFieldPaths}
                  isLoaded={targetLoaded}
                  hasMore={Boolean(hasMoreByTarget?.[targetKey])}
                  isLoadingMore={loadingTargetKey === targetKey}
                  onLoadMore={onLoadMoreBand}
                  compact={compact}
                />
              );
            })}
          </article>
        );
      })}
      </div>
    </PreviewViewportProvider>
  );
};

interface RunBandProps {
  videoId: string;
  band: ResultRunBand;
  selectedResultKey?: string | null;
  occurrenceContext?: string;
  selectedOccurrenceKey?: string | null;
  onResultOccurrenceClick?: (result: SearchResult, occurrenceKey: string) => void;
  onResultClick: (result: SearchResult) => void;
  onMarkerChange?: (result: SearchResult, marker: MarkerInfo) => void;
  isVectorSearchTile?: boolean;
  prioritizedFieldPaths?: string[];
  isLoaded: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore?: (videoId: string, runId: string) => void;
  compact?: boolean;
}

const RunBand = memo<RunBandProps>(({
  videoId,
  band,
  selectedResultKey,
  occurrenceContext,
  selectedOccurrenceKey,
  onResultOccurrenceClick,
  onResultClick,
  onMarkerChange,
  isVectorSearchTile,
  prioritizedFieldPaths,
  isLoaded,
  hasMore,
  isLoadingMore,
  onLoadMore,
  compact = false,
}) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: -1 | 1) => {
    stripRef.current?.scrollBy({
      left: direction * Math.max(280, (stripRef.current?.clientWidth || 500) * 0.8),
      behavior: 'smooth',
    });
  };

  return (
    <section
      className="search-run-band"
      data-run-id={band.runId}
      aria-label={`${band.label} results`}
    >
      <div className="search-run-band-header">
        <div className="search-run-label">
          {compact ? (
            <strong>{isLoaded ? `${band.results.length} segment${band.results.length === 1 ? '' : 's'}` : 'Loading segments…'}</strong>
          ) : (
            <>
              <span>{band.label}</span>
              {band.executedAt && <time>{new Date(band.executedAt).toLocaleDateString()}</time>}
              <strong>{isLoaded ? band.results.length : '…'}</strong>
            </>
          )}
        </div>
        <span className="search-run-band-rule" data-testid="run-band-rule" aria-hidden="true" />
        <div className="search-gallery-scroll-actions">
          <button type="button" onClick={() => scroll(-1)} aria-label={`Scroll ${band.label} left`}>
            <ChevronLeft size={16} />
          </button>
          <button type="button" onClick={() => scroll(1)} aria-label={`Scroll ${band.label} right`}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div
        className="search-run-strip"
        ref={stripRef}
        tabIndex={0}
        aria-label={`${band.label} segments`}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          scroll(event.key === 'ArrowLeft' ? -1 : 1);
        }}
      >
        {band.results.length === 0 && (
          <span className="search-run-empty">
            {isLoaded ? 'No segments in this run.' : 'Loading segments…'}
          </span>
        )}
        {band.results.map((result) => {
          const stableOccurrenceId = result.sql_row_index !== undefined
            ? `sql-row-${result.sql_row_index}`
            : result.result_id;
          const tileKey = [
            occurrenceContext,
            videoId,
            band.runId,
            result.segment_id || result.result_id,
            stableOccurrenceId,
          ].filter(Boolean).join(':');
          return (
            <DeferredSearchResultCard
              key={tileKey}
              result={result}
              tileKey={tileKey}
              onResultClick={(clicked) => (
                onResultOccurrenceClick
                  ? onResultOccurrenceClick(clicked, tileKey)
                  : onResultClick(clicked)
              )}
              onMarkerChange={onMarkerChange}
              selectedResultKey={occurrenceContext
                ? (selectedOccurrenceKey === tileKey ? getSearchResultKey(result) : null)
                : selectedResultKey}
              isVectorSearchTile={isVectorSearchTile}
              prioritizedFieldPaths={prioritizedFieldPaths}
              previewProjection={makeSegmentPreviewProjection(result)}
            />
          );
        })}
        {hasMore && (
          <button
            type="button"
            className="search-run-load-more"
            disabled={isLoadingMore}
            onClick={() => onLoadMore?.(videoId, band.runId)}
          >
            {isLoadingMore ? 'Loading…' : 'Load more segments'}
          </button>
        )}
      </div>
    </section>
  );
});

RunBand.displayName = 'RunBand';

export default memo(SearchResultsGallery);
